import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/hooks/use-auth";
import { adminSeoAuditRuns, adminRunSeoAudit } from "@/lib/admin-seo-audit.functions";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/seo-audit")({
  head: () => ({ meta: [{ title: "Admin — SEO-audit" }, { name: "robots", content: "noindex" }] }),
  component: SeoAuditPage,
});

function SeoAuditPage() {
  const { isAdmin, loading } = useAuth();
  const runs = useServerFn(adminSeoAuditRuns);
  const runAudit = useServerFn(adminRunSeoAudit);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-seo-audit-runs"],
    queryFn: () => runs(),
    enabled: isAdmin,
  });

  const mutation = useMutation({
    mutationFn: () => runAudit(),
    onSuccess: (r) => {
      if (r.ok) toast.success(`SEO-audit OK — ${r.totalUrls} URLs, geen fouten.`);
      else toast.warning(`${r.issueCount} bevinding(en) op ${r.totalUrls} URLs.`);
      qc.invalidateQueries({ queryKey: ["admin-seo-audit-runs"] });
      qc.invalidateQueries({ queryKey: ["admin-seo-audit-latest"] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Audit mislukt"),
  });

  if (loading) return <div className="p-8">Laden…</div>;
  if (!isAdmin) return <div className="p-8">Geen toegang.</div>;

  const latest = data?.runs?.[0];

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold">SEO-audit</h1>
          <p className="text-muted-foreground">
            Controleert title, meta description, canonical en og-tags voor alle URLs in de sitemap.
          </p>
        </div>
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${mutation.isPending ? "animate-spin" : ""}`} />
          Nu uitvoeren
        </button>
      </div>

      {latest && (
        <div className="rounded-xl border bg-card p-5">
          <div className="flex items-center gap-2 mb-3">
            {latest.ok ? (
              <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">
                <CheckCircle2 className="h-3 w-3" /> OK
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">
                <AlertTriangle className="h-3 w-3" /> Fouten
              </span>
            )}
            <h2 className="font-semibold">Laatste run</h2>
            <span className="text-xs text-muted-foreground">
              {new Date(latest.ran_at).toLocaleString("nl-NL")} · {latest.total_urls} URLs · {latest.issue_count} bevindingen
            </span>
          </div>
          {latest.summary && Object.keys(latest.summary).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {Object.entries(latest.summary as Record<string, number>).map(([code, n]) => (
                <span key={code} className="text-xs rounded bg-muted px-2 py-0.5">
                  <strong>{n}</strong> × {code}
                </span>
              ))}
            </div>
          )}
          {Array.isArray(latest.issues) && latest.issues.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-left py-1 pr-2">Sev</th>
                    <th className="text-left py-1 pr-2">Code</th>
                    <th className="text-left py-1 pr-2">URL</th>
                    <th className="text-left py-1">Melding</th>
                  </tr>
                </thead>
                <tbody>
                  {(latest.issues as any[]).slice(0, 100).map((i, idx) => (
                    <tr key={idx} className="border-t align-top">
                      <td className="py-1 pr-2">
                        <span className={`rounded px-1.5 py-0.5 ${i.severity === "error" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                          {i.severity}
                        </span>
                      </td>
                      <td className="py-1 pr-2 font-mono">{i.code}</td>
                      <td className="py-1 pr-2 break-all font-mono">{i.url.replace("https://elvion-group.nl", "")}</td>
                      <td className="py-1">{i.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {latest.issues.length > 100 && (
                <p className="text-xs text-muted-foreground mt-2 italic">+ {latest.issues.length - 100} meer bevindingen</p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="rounded-xl border bg-card p-5">
        <h2 className="font-semibold mb-3">Recente runs</h2>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Laden…</p>
        ) : !data?.runs?.length ? (
          <p className="text-sm text-muted-foreground">Nog geen runs.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground uppercase">
              <tr>
                <th className="text-left pb-2">Wanneer</th>
                <th className="text-left pb-2">Status</th>
                <th className="text-right pb-2">URLs</th>
                <th className="text-right pb-2">Bevindingen</th>
              </tr>
            </thead>
            <tbody>
              {data.runs.map((r: any) => (
                <tr key={r.id} className="border-t">
                  <td className="py-2">{new Date(r.ran_at).toLocaleString("nl-NL")}</td>
                  <td className="py-2">
                    {r.ok ? (
                      <span className="text-emerald-700">OK</span>
                    ) : (
                      <span className="text-red-700">Fouten</span>
                    )}
                  </td>
                  <td className="py-2 text-right">{r.total_urls}</td>
                  <td className="py-2 text-right">{r.issue_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
